import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, X, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import useSEO from '../../hooks/useSEO';
import { inventoryTitle } from '../../utils/cityTitle';
import BrowseLinks from '../../components/BrowseLinks';
import { CITY_SEO } from './CityLanding';
import VehicleCard from '../../components/VehicleCard';
import { getShortlist } from '../../utils/buyerCollections';
import './BrowseVehicles.css';
import DealerCTA from '../../components/DealerCTA';
import { vehiclePath } from '../../utils/vehicleSlug';

// Indexable city pages only — same source as the footer and homepage.
const BROWSE_CITIES = Object.keys(CITY_SEO).filter((slug) => !CITY_SEO[slug].noindex);

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SkeletonCard = () => (
  <div className="vehicle-card skeleton-card" aria-hidden="true">
    <div className="card-img-wrap skeleton-box" />
    <div className="card-body">
      <div className="skeleton-line skeleton-line-title" />
      <div className="skeleton-line skeleton-line-sm" />
      <div className="skeleton-line skeleton-line-price" />
    </div>
  </div>
);

// Supporting copy for the unfiltered /vehicles view. This page is where the
// generic buy-intent queries land ("buy taxi car", "taxi for sale", "second
// hand taxi") and it used to answer them with a 414-word shell headed "Browse
// Vehicles", so Google preferred the homepage and ranked it on page 2.
// Rendered ONLY when no filter is applied: the filtered URLs (?category=,
// ?city=, ?brand=…) would otherwise repeat this identical block across dozens
// of parameterised URLs, which is exactly the duplicate pattern that got the
// city pages declined once already.
const BUY_INTRO = [
  'Buying a used taxi is a different decision from buying a used car. You are buying an earning asset, so what matters is not the badge but the running cost per kilometre, how long the permit and fitness have left, and whether the model has the resale demand to get your money back when you upgrade. A commercial car that looks tired can still be the better buy if its papers are clean and its running costs are low.',
  'Every listing on Buy Sell Taxi shows the asking price, specifications and location before you contact anyone, and listings marked BST Verified have been inspected by our team. Use the filters to narrow by category, fuel type, brand, budget and city — CNG hatchbacks and sedans for app-cab work, 7-seaters like the Ertiga and Innova for airport and outstation duty, and Eecos and tempo travellers for shared and staff transport.',
];

const BUY_FAQS = [
  { q: 'How much does a second hand taxi cost in India?', a: 'Most used commercial cars sell between ₹2 lakh and ₹12 lakh. An older Eeco or Wagon R CNG starts around ₹2.5–4 lakh, a Swift Dzire CNG — the most common taxi in India — runs ₹4–7 lakh, an Ertiga ₹7–11 lakh, and a Toyota Innova Crysta ₹12–22 lakh depending on year and kilometres.' },
  { q: 'What should I check before buying a used taxi?', a: 'Check five things in order: how much permit and fitness validity is left, whether the odometer reading matches the service history, the remaining permissible age (10 years for diesel and 15 for petrol across Delhi NCR), that the RC is commercial and matches the seller, and the actual running cost per kilometre for that fuel type.' },
  { q: 'Can I buy a taxi car without a commercial licence?', a: 'Yes — you can own a commercial vehicle on a normal licence. You only need a commercial licence with a PSV badge to drive it for hire yourself. Many owners buy one or more taxis and employ badged drivers instead of driving.' },
  { q: 'Is a CNG or diesel taxi the better buy?', a: 'CNG for city and app-cab work, at roughly ₹3.5–4 per km against ₹5.5 for diesel, and it sidesteps the tighter 10-year diesel age limit in NCR. Diesel still earns its keep on outstation and highway routes where CNG stations are sparse and range matters.' },
  { q: 'Do the taxis listed here come with a valid permit?', a: 'Permit status is shown on each listing, and it varies — some vehicles carry a live contract-carriage or All India Tourist Permit, others have lapsed papers and are priced accordingly. Always confirm the remaining validity on the document itself before you pay; a lapsed permit means RTO work and fees before the car can earn.' },
  { q: 'Where can I buy a used taxi near me?', a: 'Most of Buy Sell Taxi\'s live stock is in Delhi NCR — Delhi and Gurugram. Filter by city above, or open your city page to see local stock alongside vehicles from nearby markets.' },
];

const BrowseSeoContent = () => (
  <section className="browse-seo" data-testid="browse-seo-content">
    <h2>Buying a Used Taxi in India</h2>
    {BUY_INTRO.map((p, i) => <p key={i}>{p}</p>)}
    <h2>Frequently Asked Questions</h2>
    <div className="browse-seo-faqs">
      {BUY_FAQS.map(({ q, a }, i) => (
        <details key={i} className="browse-seo-faq">
          <summary>{q}</summary>
          <p>{a}</p>
        </details>
      ))}
    </div>
  </section>
);

const BrowseVehicles = () => {
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [sponsored, setSponsored] = useState([]);
  const [shuffledSponsored, setShuffledSponsored] = useState([]);
  const [shortlist, setShortlist] = useState(() => getShortlist());
  const [showShortlist, setShowShortlist] = useState(false);
  const [featured, setFeatured] = useState([]);
  const [spIdx, setSpIdx] = useState(0);
  const spOuterRef = useRef(null);
  const [spSlide, setSpSlide] = useState({ cardW: 220, vis: 4 });
  const SP_GAP = 16;

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    brand: searchParams.get('brand') || '',
    fuel_type: searchParams.get('fuel_type') || '',
    transmission: searchParams.get('transmission') || '',
    state: searchParams.get('state') || '',
    city: searchParams.get('city') || '',
    year_min: searchParams.get('year_min') || '',
    year_max: searchParams.get('year_max') || '',
    price_min: searchParams.get('price_min') || '',
    price_max: searchParams.get('price_max') || '',
    search: searchParams.get('search') || '',
    sort: searchParams.get('sort') || 'newest',
    page: parseInt(searchParams.get('page') || '1')
  });

  // The plain /vehicles URL is the one that should own the generic buy-intent
  // queries; any filter or page-2 view is a narrower slice and gets neither the
  // supporting copy nor the FAQ schema.
  const isUnfiltered = useMemo(() => (
    !filters.category && !filters.brand && !filters.fuel_type && !filters.transmission &&
    !filters.state && !filters.city && !filters.year_min && !filters.year_max &&
    !filters.price_min && !filters.price_max && !filters.search && filters.page === 1
  ), [filters]);

  const pageHeading = useMemo(() => {
    // "Buy Taxi Cars" leads deliberately. The metaTitle has said "Buy Taxi Car
    // in India" since the 08-05 rebuild, but the h1 said "Buy Used Taxis" — so
    // the one phrase this page exists to win was the one phrase its heading
    // never used, while the homepage took the query on brand association and
    // sat on page 2 with it. "Used" and "Commercial Vehicles" stay: they carry
    // "used taxi for sale" and "commercial taxi", which already rank well.
    if (isUnfiltered) return 'Buy Used Taxi Cars & Commercial Vehicles in India';
    const parts = [];
    if (filters.fuel_type) parts.push(filters.fuel_type);
    if (filters.brand) parts.push(filters.brand);
    parts.push(filters.category || 'Taxis & Commercial Vehicles');
    const where = filters.city || filters.state || 'India';
    return `Used ${parts.join(' ')} for Sale in ${where}`;
  }, [filters, isUnfiltered]);

  // Built from pageHeading so a filter that sets only a location can't produce
  // a title with no noun in it — filtering to Delhi alone used to title the
  // page "in Delhi for Sale", and that URL is indexed and earning 0% CTR.
  const seoTitle = useMemo(() => {
    // Live count first — same rule as the city pages, src/utils/cityTitle.js.
    // "Buy Taxi Car" stays in the title: it is the query this page is meant to
    // take back from the homepage.
    if (isUnfiltered) return inventoryTitle('Buy Taxi Car in India – Second Hand Taxis for Sale', total);
    return filters.search ? `${pageHeading} – "${filters.search}"` : pageHeading;
  }, [filters.search, pageHeading, isUnfiltered, total]);

  const seoDescription = useMemo(() => {
    if (isUnfiltered) {
      return `Buy a used taxi in India from ₹2 lakh. ${total > 0 ? total + ' commercial' : 'Commercial'} vehicles for sale — CNG Dzire, Ertiga, Innova, Eeco — with photos and prices. BST Verified listings are inspected by our team.`;
    }
    const cat = filters.category || 'commercial vehicles';
    const loc = filters.city || filters.state || 'India';
    const brand = filters.brand ? `${filters.brand} ` : '';
    return `Browse ${total > 0 ? total + ' ' : ''}${brand}${cat} for sale in ${loc}. Verified listings with photos, prices & test drive options on Buy Sell Taxi.`;
  }, [filters, total, isUnfiltered]);

  const seoUrl = useMemo(() => {
    const base = 'https://www.buyselltaxi.com/vehicles';
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.state) params.set('state', filters.state);
    if (filters.city) params.set('city', filters.city);
    if (filters.fuel_type) params.set('fuel_type', filters.fuel_type);
    if (filters.search) params.set('search', filters.search);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }, [filters]);

  // Every filter permutation is a slice of the same inventory, so each one
  // pointing a canonical at itself just splits the page's signals across dozens
  // of URLs — /vehicles?city=Delhi was indexed separately and sat at position
  // 13.2 with zero clicks while /buy-taxi-delhi earned 7.8% CTR on the same
  // intent. Filtered and paginated views now consolidate onto the clean URL;
  // seoUrl keeps the real address for breadcrumb and ItemList entries.
  const canonicalUrl = 'https://www.buyselltaxi.com/vehicles';

  const seoBreadcrumb = useMemo(() => {
    const BASE = 'https://www.buyselltaxi.com';
    const items = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
      { '@type': 'ListItem', position: 2, name: 'Browse Vehicles', item: `${BASE}/vehicles` },
    ];
    const label = [filters.category, filters.brand, filters.city || filters.state].filter(Boolean).join(' ');
    if (label) {
      items.push({ '@type': 'ListItem', position: 3, name: `${label} for Sale`, item: seoUrl });
    }
    return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
  }, [filters, seoUrl]);

  const seoItemList = useMemo(() => {
    if (!listings || listings.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: seoTitle,
      description: seoDescription,
      url: seoUrl,
      numberOfItems: total,
      itemListElement: listings.slice(0, 10).map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://www.buyselltaxi.com${vehiclePath(v)}`,
        name: v.title,
      })),
    };
  }, [listings, seoTitle, seoDescription, seoUrl, total]);

  // FAQ rich results only on the unfiltered view, matching the copy that is
  // actually rendered there — Google flags FAQ markup that has no visible
  // counterpart on the page.
  const seoFaq = useMemo(() => {
    if (!isUnfiltered) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: BUY_FAQS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    };
  }, [isUnfiltered]);

  useSEO({
    title: seoTitle,
    description: seoDescription,
    keywords: isUnfiltered
      ? 'buy taxi car, buy taxi vehicle, taxi for sale, second hand taxi for sale, used taxi for sale India, commercial taxi for sale, taxi car price, buy used taxi India, second hand taxi car, used commercial vehicle India'
      : `used taxi marketplace India, buy taxi online India, ${filters.category || 'used taxi'} for sale India, ${filters.brand ? filters.brand + ' taxi' : 'used taxi India'}, used commercial vehicle India, sell taxi India, second hand taxi India, buy ${filters.category || 'taxi'} India`,
    url: canonicalUrl,
    jsonLd: [seoBreadcrumb, seoItemList, seoFaq].filter(Boolean),
  });

  // Fetch sponsored + featured once on mount
  useEffect(() => {
    axios.get(`${API_URL}/buyer/featured-listings`).then(res => {
      setSponsored(res.data.sponsored || []);
      setFeatured(res.data.featured || []);
    }).catch(console.error);
  }, []);

  // Shuffle sponsored on every page change
  useEffect(() => {
    if (sponsored.length > 0) {
      setShuffledSponsored([...sponsored].sort(() => Math.random() - 0.5));
      setSpIdx(0);
    }
  }, [sponsored, filters.page]);

  // Measure sponsored carousel on mount + resize
  useEffect(() => {
    const calc = () => {
      if (!spOuterRef.current) return;
      const w = spOuterRef.current.offsetWidth;
      const vis = w < 500 ? 1 : w < 650 ? 2 : w < 900 ? 3 : 4;
      setSpSlide({ cardW: (w - (vis - 1) * SP_GAP) / vis, vis });
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [shuffledSponsored]); // eslint-disable-line

  // Keep shortlist strip in sync with hearts toggled anywhere on the page
  useEffect(() => {
    const sync = () => setShortlist(getShortlist());
    window.addEventListener('bst-collections', sync);
    return () => window.removeEventListener('bst-collections', sync);
  }, []);

  useEffect(() => { fetchVehicles(); }, [filters]); // eslint-disable-line

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await axios.get(`${API_URL}/buyer/vehicles?${params.toString()}`);
      // Fall back to the initial state for each of these. An API response that
      // is a 200 but not the expected JSON (SPA-fallback HTML, a gateway error
      // page) leaves `listings` undefined, and the `listings.filter` in the
      // memo below then throws during render — which unmounts the tree and had
      // the prerenderer capture an empty <div id="root"> for /vehicles, the
      // highest-priority page in the sitemap.
      setListings(res.data.listings || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      if (res.data.brands) setBrands(res.data.brands);
    } catch (err) {
      console.error('Fetch vehicles error:', err);
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val, page: 1 }));
  // Pagination has to be real <a href> links, not buttons. With buttons, page 2+
  // existed only as component state, so Googlebot saw 12 of 78 listings and the
  // other 66 got no internal link at all. The href carries the current filters so
  // a crawled or shared URL reproduces the view; the onClick keeps the existing
  // instant client-side paging, since filters state is not driven by the URL.
  const pageHref = (p) => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (k === 'page' || !v) return;
      if (k === 'sort' && v === 'newest') return;
      q.set(k, v);
    });
    if (p > 1) q.set('page', String(p));
    const qs = q.toString();
    return qs ? `/vehicles?${qs}` : '/vehicles';
  };

  const clearFilters = () => setFilters({ category:'', brand:'', fuel_type:'', transmission:'', state:'', city:'', year_min:'', year_max:'', price_min:'', price_max:'', search:'', sort:'newest', page:1 });

  // Featured strip only makes sense on an unfiltered first page — when the user
  // searches or filters, showing unrelated featured vehicles buries their results.
  const hasActiveFilters = Boolean(
    filters.category || filters.brand || filters.fuel_type || filters.transmission ||
    filters.state || filters.city || filters.year_min || filters.year_max ||
    filters.price_min || filters.price_max || filters.search
  );
  const showFeatured = filters.page === 1 && !hasActiveFilters;

  // Deduplicate: featured + regular in one grid (featured first by rank).
  // Only exclude featured vehicles from the grid when the featured strip is shown,
  // otherwise a featured vehicle matching the filters would vanish from results.
  const featuredIds = useMemo(() => new Set(featured.map(v => v.id)), [featured]);
  const sponsoredIds = useMemo(() => new Set(sponsored.map(v => v.id)), [sponsored]);
  const regularListings = useMemo(
    () => listings.filter(v => !(showFeatured && featuredIds.has(v.id)) && !sponsoredIds.has(v.id)),
    [listings, featuredIds, sponsoredIds, showFeatured]
  );

  return (
    <div className="browse-page" data-testid="browse-vehicles-page">
      <div className="browse-header">
        <h1>{pageHeading}</h1>
        <p>{total} vehicles found</p>
        <button className="filter-toggle mobile-only" onClick={() => setShowFilters(!showFilters)}>
          <Filter size={18} /> Filters
        </button>
      </div>

      <div className="browse-layout">
        {/* FILTER SIDEBAR */}
        <aside className={`filter-sidebar ${showFilters ? 'open' : ''}`} data-testid="filter-sidebar">
          <div className="filter-header">
            <h2>Filters</h2>
            <button className="clear-btn" onClick={clearFilters} data-testid="clear-filters">Clear All</button>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <input type="text" value={filters.search} onChange={(e) => setFilter('search', e.target.value)} placeholder="Brand, model..." data-testid="filter-search" />
          </div>

          <div className="filter-group">
            <label>Category</label>
            {['Cars', 'Mini Vans', 'Buses'].map(c => (
              <label key={c} className="checkbox-label">
                <input type="checkbox" checked={filters.category === c} onChange={() => setFilter('category', filters.category === c ? '' : c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>

          <div className="filter-group">
            <label>Brand</label>
            <select value={filters.brand} onChange={(e) => setFilter('brand', e.target.value)} data-testid="filter-brand">
              <option value="">All Brands</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label>Fuel Type</label>
            {['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'].map(f => (
              <label key={f} className="checkbox-label">
                <input type="checkbox" checked={filters.fuel_type === f} onChange={() => setFilter('fuel_type', filters.fuel_type === f ? '' : f)} />
                <span>{f}</span>
              </label>
            ))}
          </div>

          <div className="filter-group">
            <label>Transmission</label>
            {['Manual', 'Automatic'].map(t => (
              <label key={t} className="checkbox-label">
                <input type="checkbox" checked={filters.transmission === t} onChange={() => setFilter('transmission', filters.transmission === t ? '' : t)} />
                <span>{t}</span>
              </label>
            ))}
          </div>

          <div className="filter-group">
            <label>Year Range</label>
            <div className="range-row">
              <input type="number" placeholder="Min" value={filters.year_min} onChange={(e) => setFilter('year_min', e.target.value)} />
              <span>to</span>
              <input type="number" placeholder="Max" value={filters.year_max} onChange={(e) => setFilter('year_max', e.target.value)} />
            </div>
          </div>

          <div className="filter-group">
            <label>Price Range (₹)</label>
            <div className="range-row">
              <input type="number" placeholder="Min" value={filters.price_min} onChange={(e) => setFilter('price_min', e.target.value)} />
              <span>to</span>
              <input type="number" placeholder="Max" value={filters.price_max} onChange={(e) => setFilter('price_max', e.target.value)} />
            </div>
          </div>

          <div className="filter-group">
            <label>State</label>
            <input type="text" value={filters.state} onChange={(e) => setFilter('state', e.target.value)} placeholder="e.g., Maharashtra" data-testid="filter-state" />
          </div>

          <div className="filter-group">
            <label>City</label>
            <input type="text" value={filters.city} onChange={(e) => setFilter('city', e.target.value)} placeholder="e.g., Mumbai" data-testid="filter-city" />
          </div>

          <button className="mobile-only close-filters-btn" onClick={() => setShowFilters(false)}>
            <X size={16} /> Close Filters
          </button>
        </aside>

        {/* LISTINGS AREA */}
        <div className="listings-area">

          {/* 0. SHORTLIST — only when the buyer has saved vehicles */}
          {shortlist.length > 0 && (
            <div className="shortlist-strip" data-testid="shortlist-strip">
              <button
                className="shortlist-toggle"
                onClick={() => setShowShortlist(s => !s)}
                aria-expanded={showShortlist}
                data-testid="shortlist-toggle"
              >
                <Heart size={16} /> My Shortlist ({shortlist.length})
                <span className="shortlist-hint">{showShortlist ? 'Hide' : 'Show'}</span>
              </button>
              {showShortlist && (
                <div className="vehicle-grid shortlist-grid" data-testid="shortlist-grid">
                  {shortlist.map(v => (
                    <VehicleCard key={v.id} v={v} testId={`shortlist-card-${v.id}`} source="shortlist" />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 1. SORT BAR — always first */}
          <div className="sort-bar" data-testid="sort-bar">
            <span>Sort by:</span>
            {['newest', 'price_asc', 'price_desc'].map(s => (
              <button key={s} className={`sort-btn ${filters.sort === s ? 'active' : ''}`}
                onClick={() => setFilter('sort', s)} data-testid={`sort-${s}`}>
                {s === 'newest' ? 'Newest' : s === 'price_asc' ? 'Price Low-High' : 'Price High-Low'}
              </button>
            ))}
          </div>

          {/* 2. SPONSORED — arrow carousel, shuffles on page change */}
          {shuffledSponsored.length > 0 && (() => {
            const spMax = Math.max(0, shuffledSponsored.length - spSlide.vis);
            return (
              <div className="sponsored-strip" data-testid="sponsored-section">
                <div className="sponsored-strip-header">
                  <span className="sponsored-strip-label">AD</span>
                  <div className="sp-arrow-group">
                    <button
                      className="sp-arrow-btn"
                      onClick={() => setSpIdx(i => Math.max(0, i - 1))}
                      disabled={spIdx === 0}
                      aria-label="Previous sponsored"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      className="sp-arrow-btn"
                      onClick={() => setSpIdx(i => Math.min(spMax, i + 1))}
                      disabled={spIdx >= spMax}
                      aria-label="Next sponsored"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
                <div className="sponsored-carousel-outer" ref={spOuterRef}>
                  <div
                    className="sponsored-carousel-track"
                    style={{ transform: `translateX(-${spIdx * (spSlide.cardW + SP_GAP)}px)` }}
                  >
                    {shuffledSponsored.map(v => (
                      <div key={v.id} className="sp-slide" style={{ width: spSlide.cardW }}>
                        <VehicleCard v={v} tag="sponsored" testId={`sponsored-card-${v.id}`} source="browse_sponsored" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 3. COMBINED GRID: Featured (by rank) → Regular (no section break) */}
          {loading ? (
            <div className="vehicle-grid" data-testid="vehicle-grid-loading">
              {Array.from({ length: 9 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : ((!showFeatured || featured.length === 0) && regularListings.length === 0) ? (
            <div className="empty-state" data-testid="no-vehicles">
              <p>No vehicles found matching your filters. Try adjusting your search.</p>
              <button className="clear-btn" onClick={clearFilters}>Clear All Filters</button>
            </div>
          ) : (
            <>
              <div className="vehicle-grid" data-testid="vehicle-grid">
                {/* Featured cards first — ordered by rank, shown on page 1 only */}
                {showFeatured && featured.map((v, i) => (
                  <VehicleCard key={v.id} v={v} ribbon="featured" testId={`featured-card-${v.id}`} eager={i < 4} lcp={i === 0} source="browse_featured" />
                ))}
                {/* Regular listings — deduped from featured/sponsored */}
                {regularListings.map((v, i) => (
                  <VehicleCard key={v.id} v={v} testId={`browse-card-${v.id}`} eager={(showFeatured ? featured.length : 0) + i < 4} lcp={(showFeatured ? featured.length : 0) + i === 0} source="browse" />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="pagination" data-testid="pagination">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <Link key={p} to={pageHref(p)} className={`page-btn ${filters.page === p ? 'active' : ''}`}
                      aria-current={filters.page === p ? 'page' : undefined}
                      onClick={() => setFilters(prev => ({ ...prev, page: p }))}>
                      {p}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Someone scrolling a stock list is exactly who a dealer offer is for. */}
          <DealerCTA variant="card" />

          {isUnfiltered && <BrowseSeoContent />}

          {/* /vehicles linked to nothing but listings and its own pagination. */}
          <BrowseLinks cities={BROWSE_CITIES} heading="Browse by City or Model" />
        </div>
      </div>
    </div>
  );
};

export default BrowseVehicles;
